import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { api } from "../lib/api";

const HEADLINES = [
  "Bottled espresso drinks, crafted for Memphis.",
  "Small-batch lattes made for everyday moments.",
];

const DEFAULT_TILE = {
  enabled: true,
  eyebrow: "This week",
  title: "Overton Park & Crosstown",
  subtitle: "Sat – Sun · Served cold",
};

export const Hero = () => {
  const [idx, setIdx] = useState(0);
  const [tile, setTile] = useState(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % HEADLINES.length), 4200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/settings");
        if (!cancelled) setTile(r.data?.hero_tile || DEFAULT_TILE);
      } catch {
        if (!cancelled) setTile(DEFAULT_TILE);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-14 pb-20 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#8D99AE] mb-6"
          >
            <span className="h-px w-8 bg-[#8D99AE]" />
            Memphis, Tennessee
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2C1E16] min-h-[5.5rem] sm:min-h-[8rem] lg:min-h-[12rem]"
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={idx}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="block"
              >
                {HEADLINES[idx]}
              </motion.span>
            </AnimatePresence>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 text-base sm:text-lg leading-relaxed text-[#5C4E46] max-w-xl"
          >
            Fresh bottled lattes made with real espresso, simple ingredients,
            and a love for the city we call home.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              data-testid="hero-primary-cta"
              href="#popups"
              className="inline-flex items-center justify-center gap-2 bg-[#2C1E16] text-[#FAF9F6] hover:bg-[#3E2D24] transition-colors rounded-full px-7 py-4 font-medium"
            >
              <MapPin size={18} strokeWidth={1.5} />
              Find Us This Week
            </a>
            <a
              data-testid="hero-secondary-cta"
              href="#contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#2C1E16] text-[#2C1E16] hover:bg-[#2C1E16] hover:text-[#FAF9F6] transition-colors rounded-full px-7 py-4 font-medium"
            >
              Join the Text / Email List
              <ArrowRight size={18} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div className="md:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative aspect-[4/5] w-full rounded-[2rem] overflow-hidden bg-[#E8E3DD]"
          >
            <img
              data-testid="hero-image"
              src="https://images.unsplash.com/photo-1583165278997-0250ea5d72e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxiYXJpc3RhJTIwcG91cmluZyUyMGVzcHJlc3NvfGVufDB8fHx8MTc4MDg5Njg1Nnww&ixlib=rb-4.1.0&q=85"
              alt="Barista pouring espresso"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </motion.div>

          {tile?.enabled && (
            <div
              data-testid="hero-tile"
              className="hidden md:block absolute -left-6 -bottom-6 bg-white rounded-2xl border border-[#E8E3DD] shadow-[0_12px_40px_rgb(0,0,0,0.05)] px-5 py-4 max-w-[15rem]"
            >
              {tile.eyebrow && (
                <div data-testid="hero-tile-eyebrow" className="text-[10px] uppercase tracking-[0.18em] text-[#8D99AE]">
                  {tile.eyebrow}
                </div>
              )}
              {tile.title && (
                <div data-testid="hero-tile-title" className="font-display text-lg text-[#2C1E16] mt-1">
                  {tile.title}
                </div>
              )}
              {tile.subtitle && (
                <div data-testid="hero-tile-subtitle" className="text-xs text-[#5C4E46] mt-1">
                  {tile.subtitle}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
